import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Select,
  Typography,
  Modal,
  Form,
  InputNumber,
  Input,
  Row,
  Col,
  Statistic,
  Tooltip,
  Empty,
} from "antd";
import {
  WarningOutlined,
  ShoppingCartOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getShortages, generatePRFromShortage } from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const MaterialShortages = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterStage, setFilterStage] = useState(undefined);
  const [prModalVisible, setPrModalVisible] = useState(false);
  const [selectedShortage, setSelectedShortage] = useState(null);
  const [prLoading, setPrLoading] = useState(false);
  const [prForm] = Form.useForm();

  // ── Fetch ──────────────────────────────────────────────────
  const fetchShortages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStage) params.stage = filterStage;
      const result = await getShortages(params);
      setData(result);
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to load shortage report");
    } finally {
      setLoading(false);
    }
  }, [filterStage]);

  useEffect(() => {
    fetchShortages();
  }, [fetchShortages]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Generate Purchase Request ──────────────────────────────
  const openPRModal = (shortage) => {
    setSelectedShortage(shortage);
    prForm.setFieldsValue({
      quantity: shortage.total_shortage,
      unit: shortage.unit,
      notes: `Auto-generated from MRP shortage for ${shortage.material?.name || shortage.material_id}`,
    });
    setPrModalVisible(true);
  };

  const handleGeneratePR = async (values) => {
    if (!selectedShortage) return;
    try {
      setPrLoading(true);
      const result = await generatePRFromShortage({
        material_id: selectedShortage.material_id,
        quantity: values.quantity,
        unit: values.unit,
        job_ids: (selectedShortage.affected_jobs || []).map((j) => j.job_id),
        notes: values.notes,
      });
      toast.success(
        `Purchase Request ${result.purchase_request?.pr_number || ""} created`
      );
      setPrModalVisible(false);
      prForm.resetFields();
      setSelectedShortage(null);
      fetchShortages();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create purchase request"
      );
    } finally {
      setPrLoading(false);
    }
  };

  // ── Aggregated shortage table ──────────────────────────────
  const columns = [
    {
      title: "Material",
      key: "material",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.material?.name || r.material_id}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.material?.code} • {r.material?.category}
          </Text>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "material_type",
      key: "type",
      width: 100,
      render: (t) => <Tag>{t}</Tag>,
    },
    {
      title: "Total Shortage",
      key: "shortage",
      width: 150,
      sorter: (a, b) => (a.total_shortage || 0) - (b.total_shortage || 0),
      defaultSortOrder: "descend",
      render: (_, r) => (
        <Text type="danger" strong>
          {r.total_shortage?.toLocaleString()} {r.unit}
        </Text>
      ),
    },
    {
      title: "Affected Jobs",
      key: "jobs",
      width: 300,
      render: (_, r) => (
        <Space size={4} wrap>
          {(r.affected_jobs || []).map((j) => (
            <Tooltip key={j.job_id} title={`Shortage: ${j.shortage_qty} • Priority: ${j.priority}`}>
              <Tag
                color={j.priority === "high" || j.priority === "urgent" ? "red" : "default"}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/job/${j.job_id}`)}
              >
                {j.job_number}
              </Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, r) => (
        <Button
          type="primary"
          size="small"
          icon={<ShoppingCartOutlined />}
          onClick={() => openPRModal(r)}
        >
          Create PR
        </Button>
      ),
    },
  ];

  const totalShortages = data?.total_shortages || 0;
  const aggregated = data?.aggregated || [];

  return (
    <>
      <PageTitle title="Back" subtitle="Material Shortages" />

      <Card
        title={
          <Space>
            <WarningOutlined style={{ color: "#ff4d4f" }} />
            <span>Material Shortages</span>
            {totalShortages > 0 && (
              <Tag color="error">{totalShortages} total</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Select
              placeholder="Filter by stage"
              allowClear
              style={{ width: 180 }}
              value={filterStage}
              onChange={setFilterStage}
            >
              <Select.Option value="corrugation">Corrugation</Select.Option>
              <Select.Option value="sheet_cutting">Sheet Cutting</Select.Option>
              <Select.Option value="pasting">Pasting</Select.Option>
              <Select.Option value="printing">Printing</Select.Option>
              <Select.Option value="casting">Casting</Select.Option>
              <Select.Option value="joining">Joining</Select.Option>
            </Select>
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchShortages}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {/* Summary row */}
        {aggregated.length > 0 && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic
                title="Unique Materials"
                value={aggregated.length}
                valueStyle={{ color: "#ff4d4f" }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total Shortages"
                value={totalShortages}
                valueStyle={{ color: "#ff4d4f" }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Paper Shortages"
                value={
                  aggregated.filter((s) => s.material_type === "PAPER").length
                }
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Other Shortages"
                value={
                  aggregated.filter((s) => s.material_type !== "PAPER").length
                }
              />
            </Col>
          </Row>
        )}

        {aggregated.length > 0 ? (
          <Table
            columns={columns}
            dataSource={aggregated}
            rowKey="material_id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            size="middle"
          />
        ) : (
          <Empty
            description={
              loading
                ? "Loading..."
                : "No material shortages — all job requirements are fulfilled"
            }
          />
        )}
      </Card>

      {/* Generate Purchase Request Modal */}
      <Modal
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>
              Generate Purchase Request —{" "}
              {selectedShortage?.material?.name || ""}
            </span>
          </Space>
        }
        open={prModalVisible}
        onCancel={() => {
          setPrModalVisible(false);
          prForm.resetFields();
          setSelectedShortage(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={prForm} layout="vertical" onFinish={handleGeneratePR}>
          <Form.Item
            name="quantity"
            label="Order Quantity"
            rules={[
              { required: true, message: "Enter quantity" },
              { type: "number", min: 0.01, message: "Must be > 0" },
            ]}
          >
            <InputNumber min={0.01} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="unit" label="Unit">
            <Input disabled />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>

          {/* Show affected jobs */}
          {selectedShortage?.affected_jobs?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Affected jobs:</Text>
              <div style={{ marginTop: 4 }}>
                <Space size={4} wrap>
                  {selectedShortage.affected_jobs.map((j) => (
                    <Tag key={j.job_id} color="orange">
                      {j.job_number} ({j.shortage_qty} {selectedShortage.unit})
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={prLoading}>
                Create Purchase Request
              </Button>
              <Button
                onClick={() => {
                  setPrModalVisible(false);
                  setSelectedShortage(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MaterialShortages;
